import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, LinearProgress, Chip, Alert, Collapse, IconButton, Divider } from '@mui/material';
import { TrendingUp as TrendingUpIcon, Assessment as AssessmentIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon, Visibility as VisibilityIcon, AttachMoney as AttachMoneyIcon, ExpandMore, ExpandLess, KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon, Science as ScienceIcon, PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Category as CategoryIcon, Group as GroupIcon } from '@mui/icons-material';
import DateRangeFilter, { getPresetDates } from '../../../components/DateRangeFilter';
import { tableStyles, nestedTableStyles } from '../reportingStyles';
import { useReporting } from '../ReportingContext';

const OfferStatsTab = () => {
  const {
    offerStatsData,
    offerStatsLoading,
    offerStatsSummary,
    filterOptions,
    offerPreset,
    filters,
    setOfferPreset,
    setFilters,
    handleFilterChange,
    handleClearFilters,
    handleSort,
    getSortedData,
    renderSortIcon,
  } = useReporting();

  return (
  <Box>
    {/* Filters Section */}
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <DateRangeFilter
              label="Date Range"
              value={offerPreset}
              size="small"
              onChange={({ startDate, endDate }, preset) => {
                setOfferPreset(preset);
                handleFilterChange('startDate', startDate);
                handleFilterChange('endDate', endDate);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Network / Company</InputLabel>
              <Select
                value={filters.networkCompany}
                onChange={(e) => handleFilterChange('networkCompany', e.target.value)}
                label="Network / Company"
                displayEmpty
                notched
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.networks.map((network) => (
                  <MenuItem key={network} value={network}>
                    {network}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Brand Name</InputLabel>
              <Select
                value={filters.brandName}
                onChange={(e) => handleFilterChange('brandName', e.target.value)}
                label="Brand Name"
                displayEmpty
                notched
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Offer</InputLabel>
              <Select
                value={filters.offerId}
                onChange={(e) => handleFilterChange('offerId', e.target.value)}
                label="Offer"
                displayEmpty
                notched
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.offers.map((offer) => (
                  <MenuItem key={offer.id} value={offer.id}>
                    {offer.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={offerStatsLoading}
                sx={{ minWidth: 120 }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    {/* Summary Cards */}
    {/* Summary Cards moved to table rows */}

    {/* Data Table */}
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Offer Performance Report
        </Typography>
        {offerStatsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={tableStyles.headerRow}>
                  <TableCell
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('networkCompany')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Network / Company
                      {renderSortIcon('networkCompany')}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('brandName')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Brand Name
                      {renderSortIcon('brandName')}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('offerTitle')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Offer
                      {renderSortIcon('offerTitle')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('clicks')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Clicks
                      {renderSortIcon('clicks')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('conversions')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Sales
                      {renderSortIcon('conversions')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('cpa')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      CPA
                      {renderSortIcon('cpa')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={tableStyles.sortableHeaderCell}
                    onClick={() => handleSort('totalPayout')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Revenue
                      {renderSortIcon('totalPayout')}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Summary row aligned with columns */}
                <TableRow sx={{ backgroundColor: '#EFF6FF' }}>
                  <TableCell colSpan={3} sx={{ ...tableStyles.bodyCell, fontWeight: 'bold', color: '#1E40AF' }}>
                    TOTAL (ALL OFFERS)
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                    {offerStatsSummary.totalClicks.toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                    {offerStatsSummary.totalConversions.toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                    ${(offerStatsSummary.averageCPA || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableStyles.bodyCell, fontWeight: '600', color: '#1E40AF' }}>
                    ${(offerStatsSummary.totalRevenue || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
                {offerStatsData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={tableStyles.emptyRow}>
                      <Typography variant="body1" color="text.secondary">
                        No data available for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedData().map((row, index) => (
                    <TableRow
                      key={index}
                      sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                    >
                      <TableCell sx={tableStyles.bodyCell}>
                        {row.networkCompany || 'N/A'}
                      </TableCell>
                      <TableCell sx={tableStyles.bodyCell}>
                        {row.brandName || 'N/A'}
                      </TableCell>
                      <TableCell sx={tableStyles.bodyCell}>
                        {row.offerTitle || 'N/A'}
                      </TableCell>
                      <TableCell align="center" sx={tableStyles.bodyCell}>
                        {row.clicks.toLocaleString()}
                      </TableCell>
                      <TableCell align="center" sx={tableStyles.bodyCell}>
                        {row.conversions.toLocaleString()}
                      </TableCell>
                      <TableCell align="center" sx={tableStyles.bodyCell}>
                        ${row.cpa.toFixed(2)}
                      </TableCell>
                      <TableCell align="center" sx={tableStyles.bodyCell}>
                        ${row.totalPayout.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  </Box>

  );
};

export default OfferStatsTab;
